/**
 * CyberneticsService — Unified static service for all cybernetic implant logic.
 *
 * Replaces and absorbs MechadendriteService. Holds a DEFINITIONS registry
 * for all 27 cybernetics (including 5 mechadendrites) with full mechanical
 * effect resolution: conditional modifiers, talent grants, armour bonuses,
 * virtual weapons, special abilities, and body location tracking —
 * all craftsmanship-aware.
 *
 * All methods are static and pure — no internal state.
 */

import { BodyLocation, EquipmentType, WeaponClass } from '../types/character';
import type {
  ConditionalModifier,
  Craftsmanship,
  Equipment,
  RateOfFire,
  Skill,
  Talent,
  Weapon
} from '../types/character';
import { CYBERNETIC_DEFINITIONS } from './cyberneticsDefinitions';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

/** Skill or characteristic modifier within a cybernetic definition */
export interface CyberneticModifierDef {
  /** kebab-case skill ID or characteristic key (e.g. "medicae", "strength") */
  targetId: string;
  value: number;
  condition: string;
}

/** Talent granted by a cybernetic */
export interface CyberneticTalentGrantDef {
  talentId: string;
  talentName: string;
  specialization?: string;
}

/** Armour bonus from a cybernetic */
export interface CyberneticArmourBonusDef {
  locations: BodyLocation[];
  armourPoints: number;
}

/** Virtual weapon entry (mechadendrites) */
export interface CyberneticWeaponDef {
  class: WeaponClass;
  range: number;
  rateOfFire: RateOfFire;
  damage: string;
  damageType: string;
  penetration: number;
  clip: number;
  reload: string;
  special: string[];
  fireRestriction?: string;
}

/** Craftsmanship-variant effects for a single tier */
export interface CyberneticCraftsmanshipEffects {
  // Optional: most tiers grant none of these, and 178 `field: []` literals in
  // cyberneticsDefinitions said so explicitly. Absent means empty.
  modifiers?: CyberneticModifierDef[];
  talentGrants?: CyberneticTalentGrantDef[];
  armourBonuses?: CyberneticArmourBonusDef[];
  specialAbilities: string[];
}

/** Full definition for one cybernetic */
export interface CyberneticDefinition {
  /** Matches sourceId in items.json */
  id: string;
  name: string;
  defaultLocation: CyberneticInstallLocation;
  /** Only present for mechadendrites */
  proficiencyCategory?: 'Weapon' | 'Utility';
  /** Only present for mechadendrites with built-in weapons */
  weaponEntry?: CyberneticWeaponDef;
  /** Effects keyed by craftsmanship tier */
  craftsmanshipEffects: Partial<Record<Craftsmanship, CyberneticCraftsmanshipEffects>>;
}

/**
 * Where a cybernetic is installed. A superset of the six hit locations: it also
 * allows Shoulder, Spine, Internal and free text. Distinct from BodyLocation in
 * types/character.ts - see INSTALLED_LOCATION_TO_ARMOR for the mapping used when
 * a cybernetic has to affect armour or damage at a specific hit location.
 *
 * NOTE: Shoulder, Spine and Internal have no BodyLocation mapping, so armour
 * bonuses on a cybernetic installed there fall back to the definition's default
 * locations. See TODO.md "Cybernetic install locations are not fully
 * attributable to a hit location".
 */
export type CyberneticInstallLocation =
  | 'Head'
  | 'Left Arm'
  | 'Right Arm'
  | 'Body'
  | 'Left Leg'
  | 'Right Leg'
  | 'Shoulder'
  | 'Spine'
  | 'Internal'
  | string;

/** Virtual talent returned by the service */
export interface VirtualTalent {
  talentId: string;
  talentName: string;
  specialization?: string;
  /** Source attribution, e.g. "Cybernetic: Bionic Heart" */
  source: string;
}

/** Armour bonus returned by the service */
export interface CyberneticArmourBonus {
  sourceId: string;
  sourceName: string;
  locations: BodyLocation[];
  armourPoints: number;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export class CyberneticsService {
  /**
   * Registry of cybernetic definitions keyed by sourceId.
   * Populated from cyberneticsDefinitions.ts across tasks 5.1–5.4.
   */
  static readonly DEFINITIONS: Record<string, CyberneticDefinition> = {
    ...CYBERNETIC_DEFINITIONS
  };

  /**
   * Characteristic keys — modifiers targeting these always appear
   * regardless of the character's skill list.
   */
  private static readonly CHARACTERISTIC_KEYS = new Set<string>([
    'weaponSkill',
    'ballisticSkill',
    'strength',
    'toughness',
    'agility',
    'intelligence',
    'perception',
    'willpower',
    'fellowship'
  ]);

  // -----------------------------------------------------------------------
  // Identification
  // -----------------------------------------------------------------------

  /**
   * Check if an equipment item is a cybernetic by verifying its sourceId
   * exists in the DEFINITIONS registry and its type is Cybernetic.
   */
  static isCybernetic(item: Equipment): boolean {
    return (
      item.sourceId in CyberneticsService.DEFINITIONS && item.type === EquipmentType.Cybernetic
    );
  }

  /**
   * Check if an equipment item is a mechadendrite — a cybernetic whose
   * definition includes a proficiencyCategory field.
   */
  static isMechadendrite(item: Equipment): boolean {
    if (!CyberneticsService.isCybernetic(item)) return false;
    const def = CyberneticsService.DEFINITIONS[item.sourceId];
    return def?.proficiencyCategory !== undefined;
  }

  // -----------------------------------------------------------------------
  // Effect Resolution
  // -----------------------------------------------------------------------

  /**
   * Generate conditional modifiers for all equipped cybernetics.
   *
   * For each equipped cybernetic, resolves craftsmanship-tier effects
   * and produces a ConditionalModifier for each modifier definition:
   * - Characteristic-targeting modifiers are always included.
   * - Skill-targeting modifiers are only included when the character
   *   possesses that skill in their skills array.
   *
   * ID pattern:  "cybernetic-{sourceId}-{targetId}-{value}"
   * Source:      "Cybernetic: {definition.name}"
   *
   * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7
   */
  static getConditionalModifiers(equipment: Equipment[], skills: Skill[]): ConditionalModifier[] {
    const modifiers: ConditionalModifier[] = [];

    for (const item of equipment) {
      if (!CyberneticsService.isCybernetic(item) || !item.installed) continue;

      const def = CyberneticsService.DEFINITIONS[item.sourceId];
      if (!def) continue;

      const effects = CyberneticsService.resolveEffects(item.sourceId, item.craftsmanship);
      if (!effects) continue;

      for (const mod of effects.modifiers ?? []) {
        const isCharacteristic = CyberneticsService.CHARACTERISTIC_KEYS.has(mod.targetId);

        if (isCharacteristic) {
          // Characteristic-targeting modifiers always appear
          modifiers.push({
            id: `cybernetic-${item.sourceId}-${mod.targetId}-${mod.value}`,
            target: mod.targetId,
            value: mod.value,
            condition: mod.condition,
            source: `Cybernetic: ${def.name}`
          });
        } else {
          // Skill-targeting modifiers only when character has the skill
          const matchingSkill = skills.find((s) => s.id === mod.targetId);
          if (matchingSkill) {
            modifiers.push({
              id: `cybernetic-${item.sourceId}-${mod.targetId}-${mod.value}`,
              target: mod.targetId,
              value: mod.value,
              condition: mod.condition,
              source: `Cybernetic: ${def.name}`
            });
          }
        }
      }
    }

    return modifiers;
  }

  /**
   * Return virtual talents granted by equipped cybernetics.
   * Skips talents the character already possesses from other sources,
   * and deduplicates within the returned array itself.
   *
   * Requirements: 3.1, 3.2, 3.4, 3.5
   */
  static getVirtualTalents(equipment: Equipment[], existingTalents: Talent[]): VirtualTalent[] {
    const virtualTalents: VirtualTalent[] = [];

    for (const item of equipment) {
      if (!CyberneticsService.isCybernetic(item) || !item.installed) continue;

      const def = CyberneticsService.DEFINITIONS[item.sourceId];
      if (!def) continue;

      const effects = CyberneticsService.resolveEffects(item.sourceId, item.craftsmanship);
      if (!effects) continue;

      for (const grant of effects.talentGrants ?? []) {
        // Check if the talent already exists in the character's talent list
        const alreadyOwned = existingTalents.some(
          (t) =>
            t.id === grant.talentId &&
            (grant.specialization === undefined || t.specialization === grant.specialization)
        );
        if (alreadyOwned) continue;

        // Check if we've already added this talent from another cybernetic
        const alreadyAdded = virtualTalents.some(
          (vt) =>
            vt.talentId === grant.talentId &&
            (grant.specialization === undefined
              ? vt.specialization === undefined
              : vt.specialization === grant.specialization)
        );
        if (alreadyAdded) continue;

        virtualTalents.push({
          talentId: grant.talentId,
          talentName: grant.talentName,
          specialization: grant.specialization,
          source: `Cybernetic: ${def.name}`
        });
      }
    }

    return virtualTalents;
  }

  /**
   * Mapping from installedLocation strings to valid BodyLocation values.
   * Only standard body locations that correspond to armour hit zones map;
   * non-standard locations (Internal, Shoulder, Spine, free-text) do not.
   */
  private static readonly INSTALLED_LOCATION_TO_ARMOR: Record<string, BodyLocation> = {
    Head: BodyLocation.Head,
    Body: BodyLocation.Body,
    'Left Arm': BodyLocation.LeftArm,
    'Right Arm': BodyLocation.RightArm,
    'Left Leg': BodyLocation.LeftLeg,
    'Right Leg': BodyLocation.RightLeg
  };

  /**
   * Return armour bonuses from equipped cybernetics.
   * Uses installedLocation when it maps to a valid BodyLocation,
   * falling back to the definition's default locations.
   *
   * Requirements: 4.1, 4.2, 4.5, 9.7
   */
  static getArmourBonuses(equipment: Equipment[]): CyberneticArmourBonus[] {
    const bonuses: CyberneticArmourBonus[] = [];

    for (const item of equipment) {
      if (!CyberneticsService.isCybernetic(item) || !item.installed) continue;

      const def = CyberneticsService.DEFINITIONS[item.sourceId];
      if (!def) continue;

      const effects = CyberneticsService.resolveEffects(item.sourceId, item.craftsmanship);
      if (!effects) continue;

      for (const ab of effects.armourBonuses ?? []) {
        // Try to map installedLocation to a valid BodyLocation
        const overrideLocation = item.installedLocation
          ? CyberneticsService.INSTALLED_LOCATION_TO_ARMOR[item.installedLocation]
          : undefined;

        const locations = overrideLocation ? [overrideLocation] : ab.locations;

        bonuses.push({
          sourceId: item.sourceId,
          sourceName: def.name,
          locations,
          armourPoints: ab.armourPoints
        });
      }
    }

    return bonuses;
  }

  /**
   * Generate virtual Weapon objects for equipped mechadendrites
   * that have a weaponEntry in their definition.
   */
  /**
   * Generate virtual Weapon objects for equipped mechadendrites
   * that have a weaponEntry in their definition.
   *
   * Ports logic from MechadendriteService.getMechadendriteWeapons.
   */
  static getVirtualWeapons(equipment: Equipment[]): Weapon[] {
    const weapons: Weapon[] = [];

    for (const item of equipment) {
      if (!item.installed || !CyberneticsService.isMechadendrite(item)) continue;

      const def = CyberneticsService.DEFINITIONS[item.sourceId];
      if (!def?.weaponEntry) continue;

      const weaponDef = def.weaponEntry;

      const special = [...weaponDef.special];
      if (weaponDef.fireRestriction) {
        special.push(weaponDef.fireRestriction);
      }

      const damage = weaponDef.damageType
        ? `${weaponDef.damage} ${weaponDef.damageType}`
        : weaponDef.damage;

      // Resolve special abilities for the description
      const effects = CyberneticsService.resolveEffects(item.sourceId, item.craftsmanship);
      const description = effects?.specialAbilities.join('; ') ?? '';

      weapons.push({
        id: `mech-weapon-${item.sourceId}`,
        sourceId: item.sourceId,
        type: EquipmentType.Weapon,
        // Body-mounted: always wielded, consumes no hand budget.
        carryState: 'wielded' as const,
        weight: 0,
        availability: 'Average',
        description,
        rulebook: 'rogue-trader-core',
        installed: true,
        class: weaponDef.class,
        range: weaponDef.range,
        rateOfFire: { ...weaponDef.rateOfFire },
        damage,
        penetration: weaponDef.penetration,
        clip: weaponDef.clip,
        reload: weaponDef.reload,
        special
      });
    }

    return weapons;
  }

  /**
   * Return special ability descriptions for each equipped cybernetic
   * that has non-empty specialAbilities in its resolved effects.
   */
  static getSpecialAbilities(
    equipment: Equipment[]
  ): Array<{ sourceId: string; sourceName: string; abilities: string[] }> {
    const result: Array<{ sourceId: string; sourceName: string; abilities: string[] }> = [];

    for (const item of equipment) {
      if (!CyberneticsService.isCybernetic(item) || !item.installed) continue;

      const def = CyberneticsService.DEFINITIONS[item.sourceId];
      if (!def) continue;

      const effects = CyberneticsService.resolveEffects(item.sourceId, item.craftsmanship);
      if (!effects || effects.specialAbilities.length === 0) continue;

      result.push({
        sourceId: item.sourceId,
        sourceName: def.name,
        abilities: [...effects.specialAbilities]
      });
    }

    return result;
  }

  // -----------------------------------------------------------------------
  // Mechadendrite-specific (absorbed from MechadendriteService)
  // -----------------------------------------------------------------------

  /**
   * Maximum mechadendrites a character can mount, equal to their
   * Toughness Bonus (tens digit of Toughness characteristic).
   */
  static getMaxMechadendrites(toughness: number): number {
    return Math.floor(toughness / 10);
  }

  /**
   * Validate whether the character's equipped mechadendrite count
   * is within the Toughness Bonus limit.
   */
  static validateMountingLimit(
    equipment: Equipment[],
    toughness: number
  ): { current: number; max: number; isValid: boolean } {
    const current = equipment.filter(
      (item) => item.installed === true && CyberneticsService.isMechadendrite(item)
    ).length;
    const max = CyberneticsService.getMaxMechadendrites(toughness);
    return { current, max, isValid: current <= max };
  }

  /**
   * Check if a character has the required Mechadendrite Use talent
   * for a specific mechadendrite.
   */
  static hasMechadendriteUseTalent(mechadendriteSourceId: string, talents: Talent[]): boolean {
    const def = CyberneticsService.DEFINITIONS[mechadendriteSourceId];
    if (!def || !def.proficiencyCategory) return false;

    const requiredCategory = def.proficiencyCategory;
    const specializedTalentId =
      requiredCategory === 'Weapon' ? 'mechadendrite-use-weapon' : 'mechadendrite-use-utility';

    for (const talent of talents) {
      if (talent.id === specializedTalentId) return true;
      if (talent.id === 'mechadendrite-use' && talent.talentGroups?.includes(requiredCategory)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if the equipment array contains an equipped MIU Weapon Interface.
   */
  static hasMIUWeaponInterface(equipment: Equipment[]): boolean {
    return equipment.some(
      (item) => item.sourceId === 'miu-weapon-interface' && item.installed === true
    );
  }

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  /**
   * Resolve the craftsmanship-specific effects for a cybernetic.
   * Falls back to Common tier when the requested tier is not defined.
   * Returns null if the sourceId is not in DEFINITIONS.
   */
  static resolveEffects(
    sourceId: string,
    craftsmanship?: Craftsmanship
  ): CyberneticCraftsmanshipEffects | null {
    const def = CyberneticsService.DEFINITIONS[sourceId];
    if (!def) return null;

    const tier = craftsmanship ?? 'Common';
    const effects = def.craftsmanshipEffects[tier] ?? def.craftsmanshipEffects['Common'];

    if (!effects) {
      return { modifiers: [], talentGrants: [], armourBonuses: [], specialAbilities: [] };
    }

    return effects;
  }

  /**
   * Return the default body location for a cybernetic from its definition.
   */
  static getDefaultLocation(sourceId: string): CyberneticInstallLocation | undefined {
    const def = CyberneticsService.DEFINITIONS[sourceId];
    return def?.defaultLocation;
  }
}
